import React, { useState, useEffect, useRef } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import logo from "../images/logo.svg";
import MapContainer from "./layout/map";
import UserMapContainer from "./layout/userMap";
import SimpleMap from "./layout/SimpleMap";
import Menu from "./layout/menu";
import CookieConsent from "./forms/cookie-consent";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useLocation } from "../context/LocationContext"; // Import the context hook
import { useLocation as useRouterLocation } from "@reach/router"; // Router location hook
import PostFilter from "./elements/PostFilter";
import Footer from "./layout/footer";
import { usePreview } from "../context/PreviewContext";
import PostCard from "./loops/PostCard";

// Environment keys
const GOOGLE_CLIENT_ID = process.env.GATSBY_GOOGLE_CLIENT_ID || "";
const RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY || "";

/**
 * Layout Component
 *
 * The Layout component serves as the overall page wrapper, integrating:
 * - A menu navigation bar.
 * - A dynamic map container tied to the selected location.
 * - Cookie consent and Google OAuth/ReCaptcha integration.
 * - Smooth scrolling for page transitions.
 *
 * Props:
 * - `children`: The main content to render within the layout.
 */
export default function Layout({ data, children, pageContext }) {
  // Access location context and React Router location
  const { selectedLocation, setSelectedLocation } = useLocation();
  const location = useRouterLocation();
  const { title: previewTitle, featureImageUrl, step, latitude, longitude, categories, selectedCategory, excerpt, allTags, selectedTags, setLatitude, setLongitude } = usePreview();

  const handleMarkerDragEnd = React.useCallback((lat: number, lng: number) => {
    setLatitude(lat.toString());
    setLongitude(lng.toString());
  }, [setLatitude, setLongitude]);

  const isCreatePointPage = location.pathname === "/create-point/";
  const isEditPointPage = location.pathname.startsWith("/edit-point/");

  const isDashboard = location.pathname === "/dashboard/";

  const allPosts = data?.allWpPost?.nodes || [];
  const { allCategories = [] } = pageContext;

    // For the homepage, there are no pre-selected categories or tags from the URL path


  // Map state, defaulting to a fallback location
  const [mapState, setMapState] = useState({
    center: selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : [50.879, 2.6997],
    zoom: 3,
  });

  // Content wrapper reference for smooth scrolling
  const contentWrapperRef = useRef(null);

  // Query site metadata (title and description)
  const {
    wp: {
      generalSettings: { title },
    },
  } = useStaticQuery(graphql`
    query LayoutQuery {
      wp {
        generalSettings {
          title
          description
        }
      }
    }
  `);

  // Define home and dynamic routes for map resetting logic
  const isHomePage =
    [
      "/",
      "/about/",
      "/contact-us/",
      "/events/",
      "/stories/",
    ].includes(location.pathname) ||
    [
      "/category/",
      "/tag/",
      "/event/",
      "/story/",
      "/features/",
      "/collections/",
      "/types/",
    ].some((route) => location.pathname.startsWith(route));

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 720);
    };

    handleResize(); // Set initial value
    if (typeof window !== 'undefined') {
      window.addEventListener("resize", handleResize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  const shouldShowFilter = pageContext && (
    pageContext.postsByYearMonth || // ArchivePage
    pageContext.categoryId || // CategoryArchive
    pageContext.tagId || // TagArchive or CategoryTagArchive
    pageContext.currentPage || // BlogPostList (home and paginated)
    (location.pathname === '/category/' && pageContext.categories) || // CategoryList
    (location.pathname === '/tag/' && pageContext.tags) // TagList
  );
 


  // Derive filter data from location.pathname
  const getFilterDataFromPath = (pathname, allCategories) => {

    let categoryPath = null;
    let tagSlug = null;
    let mainCategoryId = null;
    let subcategoryId = null;

    // Example: /category/some-category/ or /some-category/
    // Let's try to match both /category/slug and /slug
    const categoryMatch = pathname.match(/\/(?:category\/)?([^/]+)/);
    if (categoryMatch) {
      categoryPath = categoryMatch[1];

      const foundCategory = allCategories.find(cat => cat.slug === categoryPath);

      if (foundCategory) {
        if (foundCategory.parentDatabaseId) {
          // This is a subcategory, so its parent is the main category
          const parentCategory = allCategories.find(cat => cat.databaseId === foundCategory.parentDatabaseId);
          
          if (parentCategory) {
            mainCategoryId = parentCategory.id;
            subcategoryId = foundCategory.id;
          }
        } else {
          // This is a top-level category
          mainCategoryId = foundCategory.id;
        }
      }
    }

    // Example: /tag/some-tag/
    const tagMatch = pathname.match(/\/tag\/([^/]+)/);
    if (tagMatch) {
      tagSlug = tagMatch[1];
      
    }

    
    return {
      currentCategoryPath: categoryPath,
      currentTagSlug: tagSlug,
      currentMainCategoryId: mainCategoryId,
      currentSubcategoryId: subcategoryId,
    };
  };

  const { currentCategoryPath, currentTagSlug, currentMainCategoryId, currentSubcategoryId } =
    getFilterDataFromPath(location.pathname, allCategories);

  const isAuthPage = [
    "/login",
    "/register",
    "/dashboard",
    "/create-place",
    "/create-point",
    "/create-event",
    "/create-story",
    "/edit-place",
    "/edit-point",
    "/edit-event",
    "/edit-story",
  ].some(path => location.pathname.startsWith(path));

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <div data-is-root-path={isHomePage}>
          {/* Header and Menu */}
          <div className="menu">
            <header style={{ zIndex: 66 }}>
              <Link className="header-link-home" to="/">
                <img src={logo} alt={`${title} logo`} style={{ marginTop: "5px" }} />
              </Link>
            </header>
            <Menu />
          </div>

          {isMobile && shouldShowFilter && (
            <PostFilter
              allCategories={allCategories}
              allTags={allTags}
              currentCategoryPath={currentCategoryPath}
              currentTagSlug={currentTagSlug}
              currentMainCategoryId={currentMainCategoryId}
              currentSubcategoryId={currentSubcategoryId}
              className="mobile-filter-styles"
            />
          )}
          {/* Page Content and Map */}
          <div className="pageWrapper" style={styles.pageWrapper}>
            
            {/* Map Container - Always present */}
            <div className="mapWrapper" tabIndex="-1" style={styles.mapWrapper}>
              
              {(isCreatePointPage || isEditPointPage) ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <SimpleMap
                    latitude={latitude ? parseFloat(latitude) : 50.879}
                    longitude={longitude ? parseFloat(longitude) : 2.6997}
                    zoom={13}
                    onMarkerDragEnd={handleMarkerDragEnd}
                  />
                </div>
              ) : isDashboard ? (
                <UserMapContainer
                  mapState={mapState} // Pass current map state
                  setMapState={setMapState} // Pass state setter
                  selectedLocation={selectedLocation} // Pass selectedLocation
                />
              ) : (
                <MapContainer
                  mapState={mapState} // Pass current map state
                  setMapState={setMapState} // Pass state setter
                  selectedLocation={selectedLocation} // Pass selectedLocation
                />
              )}
            </div>

            {/* Main Content Area */}
            <main
              ref={contentWrapperRef}
              className="contentWrapper"
              style={styles.contentWrapper}
            >
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, { isMobile });
                }
                return child;
              })}
              {!isAuthPage && <CookieConsent />}
              {!isAuthPage && <Footer />}
            </main>
          </div>

        </div>
      </GoogleOAuthProvider>
    </GoogleReCaptchaProvider>
  );
};
// Basic inline styles
const styles = {
  pageWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  mapWrapper: {
    flex: 1,
    backgroundColor: "#f0f0f0",
  },
  contentWrapper: {
    flex: 2,
    overflowY: "auto", // Scrollable if content overflows
  },
};
